import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useMercenary } from '../../contexts/MercenaryContext';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { useSocket } from '../../contexts/SocketContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShop, faStore, faDragon, faHandRock, faUserGroup, faTrophy, faMedal, faMap, faScaleUnbalancedFlip, faBeer, faShield, faBuilding, faBookOpen, faRankingStar, faCrown, faLayerGroup, faBoxOpen } from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as AnvilIcon } from '../../assets/icons/anvil.svg';
import { ReactComponent as ScrollIcon } from '../../assets/icons/scroll.svg';
import styles from './LeftNavBar.module.css';

const LeftNavBar: React.FC = () => {
  const { socket } = useSocket();
  const { setUser } = useAuth();
  const { mercenary, setMercenary, setMercenaryId } = useMercenary();
  const [guildNotification, setGuildNotification] = useState(false);

  useEffect(() => {
    if (mercenary?.guildId) {
      checkGuildNotifications();
    } else {
      setGuildNotification(false);
    }
  }, [mercenary?.guildId]);

  const checkGuildNotifications = async () => {
    try {
      const res = await axios.get('/api/guild/nav-notifications', { withCredentials: true });
      const { pendingApplications, needsStrategy } = res.data;
      setGuildNotification(pendingApplications > 0 || needsStrategy);
    } catch (error) {
      setGuildNotification(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.get(`${process.env.REACT_APP_API_BASE_URL || ''}/api/auth/logout`, { withCredentials: true });

      if (socket) {
        socket.emit('logout');
        socket.disconnect();
      }

      setMercenary(null);
      setMercenaryId(null);  // clears localStorage too
      setUser(null);
      window.location.href = 'https://njordheim.com';
    } catch (error) {
      console.error('Error during logout:', error);
      window.location.href = 'https://njordheim.com';
    }
  };

  return (
    <div className={styles.leftMenu}>
      <nav className={styles.navLinks}>
        {/* Battle Category */}
        <h3 className={styles.categoryTitle}>Battle</h3>
        <Link to="/beasts" className={styles.navLink}>
          <FontAwesomeIcon icon={faDragon} className={styles.icon} />
          Beasts
        </Link>
        <Link to="/gauntlet" className={styles.navLink}>
          <FontAwesomeIcon icon={faLayerGroup} className={styles.icon} />
          The Gauntlet
        </Link>
        <Link to="/duel" className={styles.navLink}>
          <FontAwesomeIcon icon={faHandRock} className={styles.icon} />
          Duel
        </Link>
        <Link to="/contract-battle" className={styles.navLink}>
          <ScrollIcon className={`${styles.icon} ${styles.svgIcon}`} />
          Contract Battles
        </Link>
        <Link to="/team-battle" className={styles.navLink}>
          <FontAwesomeIcon icon={faUserGroup} className={styles.icon} />
          Team Battles
        </Link>
        <Link to="/ranked-battle" className={styles.navLink}>
          <FontAwesomeIcon icon={faRankingStar} className={styles.icon} />
          Ranked Battles
        </Link>
        <Link to="/tournament" className={styles.navLink}>
          <FontAwesomeIcon icon={faMedal} className={styles.icon} />
          Tournaments
        </Link>
        <Link to="/guild/league" className={styles.navLink}>
          <FontAwesomeIcon icon={faShield} className={styles.icon} />
          Guild League
        </Link>

        {/* City Category */}
        <h3 className={styles.categoryTitle}>City</h3>
        <Link to="/market" className={styles.navLink}>
          <FontAwesomeIcon icon={faStore} className={styles.icon} />
          Market
        </Link>
        {(mercenary?.legend === true || mercenary?.title === 'Legend' || (mercenary?.legendSeasonIds && mercenary?.legendSeasonIds.length > 0)) && (
          <Link to="/emperors-vault" className={styles.navLink}>
            <FontAwesomeIcon icon={faCrown} className={styles.icon} />
            Emperor's Vault
          </Link>
        )}
        <Link to="/auction" className={styles.navLink}>
          <FontAwesomeIcon icon={faScaleUnbalancedFlip} className={styles.icon} />
          Auction House
        </Link>
        <Link to="/tavern" className={styles.navLink}>
          <FontAwesomeIcon icon={faBeer} className={styles.icon} />
          Stonebolt's Tavern
        </Link>
        <Link to="/work" className={styles.navLink}>
          <FontAwesomeIcon icon={faShop} className={styles.icon} />
          Work District
        </Link>
        <Link to="/craft" className={styles.navLink}>
          <AnvilIcon className={`${styles.icon} ${styles.svgIcon}`} />
          Crafting District
        </Link>
        <Link to="/vault" className={styles.navLink}>
          <FontAwesomeIcon icon={faStore} className={styles.icon} />
          Glacier Vault
        </Link>
        <Link to="/city-projects" className={styles.navLink}>
          <FontAwesomeIcon icon={faBuilding} className={styles.icon} />
          City Buildings
        </Link>
        <Link to="/guild" className={styles.navLink}>
          <FontAwesomeIcon icon={faShield} className={styles.icon} />
          Guild
          {guildNotification && <span className={styles.warningBadge}>!</span>}
        </Link>
        <Link to="/ranking" className={styles.navLink}>
          <FontAwesomeIcon icon={faTrophy} className={styles.icon} />
          Leaderboard
        </Link>
        <Link to="/combat-leaderboards" className={styles.navLink}>
          <FontAwesomeIcon icon={faTrophy} className={styles.icon} />
          Wall of Legends
        </Link>
        <Link to="/hall-of-honor" className={styles.navLink}>
          <FontAwesomeIcon icon={faTrophy} className={styles.icon} />
          Hall of Honor
        </Link>

        {mercenary?.id === 69 && (
          <Link to="/adventures" className={styles.navLink}>
            <FontAwesomeIcon icon={faMap} className={styles.icon} /> 
            Adventures
          </Link>
        )}

        {/* Help Category */}
        <h3 className={styles.categoryTitle}>Help</h3>
        <Link to="/wiki" className={styles.navLink}>
          <FontAwesomeIcon icon={faBookOpen} className={styles.icon} />
          Game Guide
        </Link>
        <Link to="/item-database" className={styles.navLink}>
          <FontAwesomeIcon icon={faBoxOpen} className={styles.icon} />
          Item Database
        </Link>
      </nav>
      <div className={styles.logoutContainer}>
        <a href="https://njordheim.com" className={styles.hubLink}>
          ◀ Game Hub
        </a>
        <button onClick={handleLogout} className={styles.logoutButton}>
          Logout
        </button>
      </div>
    </div>
  );
};

export default LeftNavBar;
